import { Injectable } from '@angular/core';
import { AgGridEvent, Column, FilterModel, GridApi, IDatasource, RowNode } from 'ag-grid-community';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserSettingValue } from '../helpers/user-setting-value';

@Injectable()
export class ListService {
  private readonly _agGridParams = new BehaviorSubject<AgGridEvent | undefined>(undefined);
  private readonly _dataSource = new BehaviorSubject<IDatasource | undefined>(undefined);
  private readonly _columns = new BehaviorSubject<Column[] | undefined>(undefined);
  private _refreshList = new Subject<boolean>();

  private defaultFilterModel: FilterModel | undefined = undefined;

  public agGridParams$: Observable<AgGridEvent | undefined> = this._agGridParams.asObservable();
  public isGridReady$: Observable<boolean> = this.agGridParams$.pipe(
    map((agGrid: AgGridEvent | undefined) => agGrid !== undefined)
  );
  public dataSource$: Observable<IDatasource | undefined> = this._dataSource.asObservable();
  public refreshList$: Observable<boolean> = this._refreshList.asObservable();

  public get gridApi(): GridApi | undefined {
    return this._agGridParams.value?.api ?? undefined;
  }

  public get dataSource(): IDatasource | undefined {
    return this._dataSource.value;
  }

  public addAgGridParams(params: AgGridEvent): void {
    this._agGridParams.next(params);
  }

  public addDataSource(dataSource: IDatasource): void {
    this._dataSource.next(dataSource);
  }

  // GridAPI calls
  public getSelectedNodeIds(): string[] {
    if (!this.gridApi) return [];
    const ids = this.gridApi.getSelectedNodes().map((rowNode: RowNode) => rowNode.id);
    return ids.filter((id): id is string => id !== undefined);
  }

  public getSelectedNodeIdsAsString(): string {
    const orderNumbers = this.getSelectedNodeIds();
    return orderNumbers ? orderNumbers.join('\n') : '-';
  }

  public restoreFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(this.defaultFilterModel ?? null);
    }
  }

  // Get/Set Columns
  public getColumns(): Observable<Column[] | undefined> {
    return this._columns.asObservable();
  }

  public setColumns(columns: Column[]): void {
    this._columns.next(columns);
  }

  // Update ColumnVisibility
  public updateColumnVisibility(settingValues: UserSettingValue[]): void {
    const visibleColumns: string[] = [];
    const invisibleColumns: string[] = [];
    (settingValues || []).forEach((column) => {
      return column.boolSetting === true ? visibleColumns.push(column.key) : invisibleColumns.push(column.key);
    });
    if (this.gridApi) {
      this.gridApi.setColumnsVisible(visibleColumns, true);
      this.gridApi.setColumnsVisible(invisibleColumns, false);
    }
  }

  // SetAll - ResetAll()
  public resetAll(): void {
    this._agGridParams.next(undefined);
    this._dataSource.next(undefined);
    this._columns.next(undefined);
    this.defaultFilterModel = undefined;
  }

  public setDefaultFilterModel(defaultFilterModel: FilterModel): void {
    this.defaultFilterModel = defaultFilterModel;
  }

  // Refresh list
  public refreshList(shouldRefresh: boolean): void {
    this._refreshList.next(shouldRefresh);
  }
}
